import React, { memo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Carousel from '@mlb/react-patterns/v4/components/Carousel/carousel';
import { TitleWrapper, ExploreWrapper, DemoWrapper, CarouselWrapper, ThingsToKnowWrapper, PaginationDots, PaginationDot, PaginationButtons, CarouselControlsWrapper } from '../../fieldvision.style';
import Image from '@mlb/react-patterns/v4/components/Image/image';
import { useTranslator } from '@gameday/utils/hooks/useTranslator';
import Button from '@mlb/react-patterns/v4/components/Button';
import { ButtonShapes } from '@mlb/react-patterns/v4/components/Button/button.types';

const assestUrl = 'https://prod-gameday.mlbstatic.com/responsive-gameday-assets/1.3.0/';

const circle = <circle cx='4' cy='4' r='4'/>;
const dotProps = {
    height: '8px',
    width: '8px',
    viewBox: '0 0 8 8',
    xmlns: 'http://www.w3.org/2000/svg'
};
const CarouselControls = ({ handleNext, handlePrevious, currentSlide, totalSlides, isMobile }) => {
    const isFirstSlide = currentSlide === 0;
    const isLastSlide = currentSlide === totalSlides - 1;
    return (
        <CarouselControlsWrapper $isMobile={isMobile}>
            <PaginationDots $isMobile={isMobile}>
                <PaginationDot active={isFirstSlide} onClick={() => handlePrevious()} {...dotProps}>
                    {circle}
                </PaginationDot>
                <PaginationDot active={isLastSlide} onClick={() => handleNext()} {...dotProps}>
                    {circle}
                </PaginationDot>
            </PaginationDots>
            {!isMobile && <PaginationButtons>
                <Button
                    handlers={{
                        onClick: handlePrevious
                    }}
                    disabled={isFirstSlide}
                    iconName={'carousel-arrow-left'}
                    shape={ ButtonShapes.HOLLOW_BORDERLESS }
                />
                <Button
                    handlers={{
                        onClick: handleNext
                    }}
                    disabled={currentSlide === totalSlides - 1}
                    iconName={'carousel-arrow-right'}
                    shape={ ButtonShapes.HOLLOW_BORDERLESS }

                />
            </PaginationButtons>}
        </CarouselControlsWrapper>
    );
};

CarouselControls.propTypes = {
    handleNext: PropTypes.func,
    handlePrevious: PropTypes.func,
    currentSlide: PropTypes.number,
    totalSlides: PropTypes.number,
    isMobile: PropTypes.bool
};

const ExploreCarousel = ({
    isSingleCol
}) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [carousel, setCarousel] = useState(null);
    const nextHandler = useCallback(() => carousel?.next(), [carousel]);
    const previousHandler = useCallback(() => carousel?.previous(), [carousel]);

    const afterSlideChange = (prev, { currentSlide: current }) => {
        setCurrentSlide(current);
    };

    const specs = [
        {
            info: useTranslator('text_fv_rotate', 'Rotate around the field to get different views of the action'),
            desktop: {
                src: `${assestUrl}images/fieldvision/tutorial_rotate_desktop.png`,
                width: '107px',
                label: 'rotate',
                title: useTranslator('fieldvision_tutorial_rotate_title_desktop', 'Click and Drag to Rotate')
            },
            mobile: {
                src: `${assestUrl}images/fieldvision/tutorial_rotate.png`,
                width: '73px',
                label: 'rotate',
                title: useTranslator('fieldvision_tutorial_rotate_title_mobile', 'Swipe to Rotate')
            }
        },
        {
            info: useTranslator('fieldvision_tutorial_zoom_subtitle', 'Get a closer view or zoom out to see more action'),
            desktop: {
                src: `${assestUrl}images/fieldvision/tutorial_scroll_desktop.png`,
                width: '35px',
                label: 'scroll',
                title: useTranslator('fieldvision_tutorial_zoom_title_desktop', 'Scroll to Zoom')
            },
            mobile: {
                src: `${assestUrl}images/fieldvision/tutorial_zoom.png`,
                width: '59px',
                label: 'zoom',
                title: useTranslator('fieldvision_tutorial_zoom_title_mobile', 'Pinch to Zoom')
            }
        },
        {
            info: useTranslator('fieldvision_tutorial_pan_subtitle', 'Move to a different position on the field'),
            desktop: {
                src: `${assestUrl}images/fieldvision/tutorial_pan_desktop.png`,
                width: '99px',
                label: 'pan',
                title: useTranslator('fieldvision_tutorial_pan_title_desktop', 'Right Click and Drag to Pan')
            },
            mobile: {
                src: `${assestUrl}images/fieldvision/tutorial_pan.png`,
                width: '60px',
                label: 'pan',
                title: useTranslator('fieldvision_tutorial_pan_title_mobile', '2-finger Drag to Pan')
            }
        },
        {
            info: useTranslator('fieldvision_tutorial_teleport_subtitle', 'Focus on a player and see the game from their perspective'),
            desktop: {
                src: `${assestUrl}images/fieldvision/tutorial_move_desktop.png`,
                width: '56px',
                label: 'move',
                title: useTranslator('fieldvision_tutorial_teleport_title_desktop', 'Double-click a player to change POV')
            },
            mobile: {
                src: `${assestUrl}images/fieldvision/tutorial_move.png`,
                width: '40px',
                label: 'move',
                title: useTranslator('fieldvision_tutorial_teleport_title_mobile', 'Double Tap a player to change POV')
            }
        }
    ];

    return (
        <CarouselWrapper $isMobile={isSingleCol}>
            <Carousel
                layout={ {
                    everything: {
                        breakpoint: { max: 4000, min: 0 },
                        items: 1
                    }
                } }
                draggable={ false }
                mobileSwipeable={ true }
                autoPlay={ false }
                arrowsOutside={ false }
                showDots={ false }
                showArrows={ false }
                infinite={ false }
                refHandler={setCarousel}
                afterChange={afterSlideChange}
            >
                <ExploreWrapper key={'fv-explore-slide'}>
                    <TitleWrapper>{useTranslator('fieldvision_tutorial_title', 'How to Explore Gameday 3D')}</TitleWrapper>
                    {specs.map((spec, index) => {
                        const data = (!isSingleCol) ? spec.desktop : spec.mobile;
                        return (
                            <DemoWrapper key={`tutorial-'${index}`}>
                                <Image
                                    src={ `${data.src}` }
                                    alt={ `${data.label}` }
                                    width={ `${data.width}` }
                                />
                                <div className={'title'}>{ data.title }</div>
                                <div className={'info'}>{ spec.info }</div>
                            </DemoWrapper>
                        );
                    })}
                </ExploreWrapper>
                <ExploreWrapper key={'fv-explore-slide_viewing'}>
                    <TitleWrapper>{useTranslator('fieldvision_things_to_know_title', 'Things to Know')}</TitleWrapper>
                    <ThingsToKnowWrapper>
                        <li>{useTranslator('fieldvision_things_to_know_body_nextgen', 'Advanced Tech: This innovative technology may load more slowly on older devices and weak network connections.')}</li>
                        <li>{useTranslator('fieldvision_things_to_know_body_beta', 'BETA: Stay tuned for improvements, including fluid player movements and exciting new features.')}</li>
                        <li>{useTranslator('fieldvision_things_to_know_body_available_highlight', 'Replay: Enjoy the best plays from every game with immersive 3D Replay.')}</li>
                        <li>{useTranslator('fieldvision_things_to_know_body_available_mlbtv', 'Live: Watch every MLB.TV Free Game of the Day live in 3D.')}</li>
                    </ThingsToKnowWrapper>
                </ExploreWrapper>
            </Carousel>
            <CarouselControls
                handleNext={nextHandler}
                handlePrevious={previousHandler}
                currentSlide={currentSlide}
                totalSlides={2}
                isMobile={isSingleCol}
            />
        </CarouselWrapper>

    );
};

ExploreCarousel.propTypes = {
    isSingleCol: PropTypes.bool
};

export default memo(ExploreCarousel);
